import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, EmptyState, Icon } from '~/components/ui';
import type { ThreadSummary } from '../api/threadsClient';
import { listThreads } from '../api/threadsClient';
import { useT } from '~/i18n';
import ThreadListItem from './ThreadListItem';

/** Tres. La sección es un vistazo, no un segundo inbox. */
const PREVIEW_SIZE = 3;

/**
 * "Mensajes recientes" en el panel: los últimos hilos con actividad y la puerta
 * al inbox completo.
 *
 * Si la llamada falla la sección DESAPARECE en vez de mostrar un error. El panel
 * es la pantalla que decide si el candidato está enviando postulaciones; un
 * bloque rojo por un endpoint que se cayó le haría creer que su cuenta está
 * rota. El inbox propio sí reporta su error, porque ahí es la pantalla entera.
 */
export default function RecentMessagesCard() {
  const t = useT('workspace').recentMessages;
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    listThreads(PREVIEW_SIZE, 0)
      .then((page) => {
        if (alive) setThreads(page.threads);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (failed) return null;
  // Sin datos todavía no se dibuja un esqueleto: el panel ya tiene su propia
  // espera y dos spinners apilados leen como una pantalla trabada.
  if (!threads) return null;

  const unread = threads.reduce((sum, thread) => sum + thread.unread_count, 0);

  return (
    <Card
      scope={t.scope}
      title={t.title}
      trail={
        <Link to="/mensajes" className="btn btn-ghost btn-sm gap-2">
          {unread > 0 && <span className="inbox__unread">{unread}</span>}
          {t.seeAll}
          <Icon name="arrow-right" />
        </Link>
      }
    >
      {threads.length === 0 ? (
        <EmptyState
          icon="inbox"
          title={t.emptyTitle}
          note={t.emptyNote}
        />
      ) : (
        <ul className="inbox inbox--compact">
          {threads.map((thread) => (
            <ThreadListItem key={thread._id} thread={thread} />
          ))}
        </ul>
      )}
    </Card>
  );
}
